import {
    LOGIN_USER,
    LOGOUT_USER,
    SET_CARD_TOKEN,
    LOGIN_AS_CUSTOMER,
    UPDATE_CUSTOMER
} from './actions';
import { doSwaggerCall } from '../hooks/useSwaggerClient';
import { updateCartDiscount } from './cart';
import { MODAL_TYPE } from '../hooks/useModal';
import {
    tryAcquireGuestLock,
    releaseGuestLock,
    startLockRefresh,
    stopLockRefresh
} from '../tools/softlock';

export function loginUser(user) {
    return { type: LOGIN_USER, user };
}

function updateCartDiscountDefaultDiscount(dispatch) {
    const defaultDiscount = window.clientData.default_discount;
    if (defaultDiscount.discount) {
        const agencyDiscount = parseFloat(defaultDiscount.discount.agencyDiscount?.amount) || 0;
        const vendorDiscount = parseFloat(defaultDiscount.discount.vendorDiscount?.amount) || 0;
        dispatch(
            updateCartDiscount({
                agencyDiscount: agencyDiscount,
                vendorDiscount: vendorDiscount,
                discountPackage: {
                    id: defaultDiscount.id
                }
            })
        );
    }
}

// this one is not exported
function loginAsCustomer(user, keepCart) {
    return (dispatch, getState) => {
        // Dispatch the login action
        dispatch({ type: LOGIN_AS_CUSTOMER, user, keepCart });

        // Auto-apply default discount if it exists and we're not keeping cart with existing discount
        if (window.clientData?.default_discount) {
            updateCartDiscountDefaultDiscount(dispatch);
        }
    };
}

export function setCardToken(cardToken) {
    return { type: SET_CARD_TOKEN, cardToken };
}

export function updateCustomer(customer) {
    return { type: UPDATE_CUSTOMER, customer };
}

export function logoutUser() {
    return { type: LOGOUT_USER };
}

export function setLoggedInUser(user) {
    return dispatch => {
        dispatch(loginUser(user));

        // default discount apply if the role is admin or agent
        if (
            window.clientData?.default_discount &&
            (user.role === 'admin' || user.role === 'agent')
        ) {
            updateCartDiscountDefaultDiscount(dispatch);
        }
    };
}

function loginAsCustomerWithIdKeepCartOption(customerId, keepCart, onError, onSuccess) {
    return (dispatch, getState) => {
        // this is a logout
        if (customerId === null) {
            // Release lock and stop refresh
            releaseGuestLock().catch(err => {
                console.error('Failed to release lock during logout:', err);
            });

            stopLockRefresh();

            if (onSuccess) {
                onSuccess?.();
            }

            return dispatch(loginAsCustomer(null, keepCart));
        }

        // normal login - lock already acquired before this point
        return doSwaggerCall('users', 'getUserById', { userId: customerId })
            .then(data => {
                // Start lock refresh after successful login
                startLockRefresh();
                dispatch(loginAsCustomer(data.guest, keepCart));
                if (onSuccess) {
                    onSuccess?.(data?.guest);
                }
            })
            .catch(error => {
                // Release lock on login failure
                releaseGuestLock().catch(lockErr => {
                    console.error('Failed to release lock after login error:', lockErr);
                });
                if (onError) {
                    onError?.(error.response.body.message);
                }
            });
    };
}

/**
 * Proceed with the actual login flow after lock is acquired
 */
function proceedWithLogin(dispatch, getState, customerId, showModal, onError, onSuccess) {
    const { cart } = getState();
    const { user } = getState();

    // no item in cart, no question
    if (cart.cartItems.length === 0) {
        return dispatch(loginAsCustomerWithIdKeepCartOption(customerId, false, onError, onSuccess));
    }
    // agent has no active "customer", tries to login to one, keep the cart
    if (user.customer === null) {
        return dispatch(loginAsCustomerWithIdKeepCartOption(customerId, true, onError, onSuccess));
    }

    showModal(MODAL_TYPE.KEEP_CART, {
        onDecided: keepCart => {
            return dispatch(
                loginAsCustomerWithIdKeepCartOption(customerId, keepCart, onError, onSuccess)
            );
        }
    });
}

/**
 * Grab guest data from the backend and perform the login flow
 * @param customerId
 * @param showModal
 * @param onError
 * @param onSuccess
 * @returns {function(*): Promise<*>}
 */
export function loginAsCustomerWithId(customerId, showModal, onError, onSuccess) {
    return async (dispatch, getState) => {
        // Logout flow
        if (customerId === null) {
            return dispatch(loginAsCustomerWithIdKeepCartOption(null, false, onError, onSuccess));
        }

        // Login flow - try to acquire lock first
        const lockResult = await tryAcquireGuestLock(customerId, false);

        if (lockResult.success) {
            // Lock acquired, proceed with login
            return proceedWithLogin(dispatch, getState, customerId, showModal, onError, onSuccess);
        }

        // Lock conflict - show modal
        showModal(MODAL_TYPE.SOFT_LOCK, {
            lockHolders: lockResult.lockHolders || [],
            onForceLogin: async () => {
                // Force acquire lock and proceed
                await tryAcquireGuestLock(customerId, true);
                proceedWithLogin(dispatch, getState, customerId, showModal, onError, onSuccess);
            },
            onCancel: () => {
                // User chose not to login
            }
        });
    };
}

export function refreshUserCapabilities() {
    return (dispatch, getState) => {
        return doSwaggerCall('users', 'getUserById', { userId: 'me' })
            .then(data => {
                // the session can be dropped while this is in flight (eg. the
                // jwt-oauth hand-off logs out first), so don't resurrect it
                const currentToken = getState().user?.user?.token;
                if (!currentToken) {
                    return;
                }
                dispatch(loginUser({ ...data.guest, token: currentToken }));
            })
            .catch(err => {
                // a stale / expired persisted token just means we are not
                // logged in - drop the session instead of throwing an
                // unhandled rejection
                if (err?.status === 401 || err?.status === 403) {
                    dispatch(logoutUser());
                    return;
                }
                console.error('Failed to refresh user capabilities:', err);
            });
    };
}

export function updateFreshCustomerData() {
    return (dispatch, getState) => {
        const customerId = getState().user.customer.id;
        const userId = getState().user.user.id;
        if (!customerId || customerId === userId || customerId === 'me') {
            return;
        }
        return doSwaggerCall('users', 'getUserById', { userId: customerId })
            .then(data => {
                dispatch(updateCustomer(data.guest));
                loginAsCustomerWithIdKeepCartOption(customerId, true)(dispatch, getState);
            })
            .catch(err => {
                if (err?.status === 401 || err?.status === 403) {
                    dispatch(logoutUser());
                    return;
                }
                console.error('Failed to refresh customer data:', err);
            });
    };
}

export function createGuest({
    firstname,
    lastname,
    email,
    additional_emails,
    phone,
    guest_field_info,
    arrival_date,
    departure_date,
    internal_notes,
    notification,
    source_id,
    onSuccess,
    onError
}) {
    return dispatch => {
        return doSwaggerCall('users', 'createUser', {
            user: {
                email,
                additional_emails,
                firstname,
                lastname,
                phone,
                guest_field_info,
                arrival_date,
                departure_date,
                internal_notes,
                notification,
                source_id
            }
        })
            .then(data => {
                dispatch(updateCustomer(data.user));
                if (onSuccess) {
                    onSuccess(data);
                }
                return data.user.id;
            })
            .then(customerId => {
                tryAcquireGuestLock(customerId, false);
                startLockRefresh();
            })
            .catch(error => {
                if (onError) {
                    onError(error.response.body.message);
                }
                stopLockRefresh();
            });
    };
}

export function updateGuest({
    id,
    firstname,
    lastname,
    email,
    additional_emails,
    phone,
    guest_field_info,
    arrival_date,
    departure_date,
    internal_notes,
    notification,
    liability_document_accepted_at,
    liability_document_name,
    source_id
}) {
    return (dispatch, getState) => {
        return doSwaggerCall('users', 'updateUser', {
            userId: id,
            user: {
                firstname,
                lastname,
                email,
                additional_emails,
                phone,
                arrival_date,
                departure_date,
                internal_notes,
                guest_field_info,
                notification,
                liability_document_accepted_at,
                liability_document_name,
                source_id
            }
        })
            .then(() => {
                return doSwaggerCall('users', 'getUserById', { userId: id });
            })
            .then(data => {
                if (id === 'me') {
                    dispatch(loginUser({ ...data.guest, token: getState().user.user.token }));
                } else {
                    dispatch(updateCustomer(data.guest));
                }
                // TODO: Check if after update we close the modal
                // dispatch(setOpenModal('none'));
            })
            .catch(console.log);
    };
}
